import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldCheck, Star, Crown, Shield, ArrowRight, IdCard } from "lucide-react";
import { TierBadge, type Tier } from "@/components/TierBadge";
import { formatMoney } from "@/lib/money";

interface Limits { tier: Tier; daily_payout_cents: number; max_gig_budget_cents: number; instant_release: boolean; description: string | null; }
interface Risk { fraud_score: number; trust_score: number; dispute_rate: number; completion_rate: number; }
interface Stats { completed: number; account_age_days: number; }

const TIER_ORDER: Tier[] = ["basic", "verified", "pro", "elite"];
const TIER_REQ: Record<Tier, string[]> = {
  basic: ["Phone verified"],
  verified: ["Government ID approved"],
  pro: ["10+ completed gigs", "Dispute rate < 5%", "30+ days on Cash Isle", "Fraud score < 30"],
  elite: ["50+ completed gigs", "Dispute rate < 2%", "90+ days on Cash Isle", "Fraud score < 10"],
};

const Verification = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tier, setTier] = useState<Tier>("basic");
  const [kyc, setKyc] = useState<string>("unverified");
  const [limits, setLimits] = useState<Limits[]>([]);
  const [risk, setRisk] = useState<Risk | null>(null);
  const [stats, setStats] = useState<Stats>({ completed: 0, account_age_days: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const [{ data: p }, { data: l }, { data: r }, { count: c }] = await Promise.all([
        supabase.from("profiles").select("tier, kyc_status, created_at").eq("user_id", user.id).maybeSingle(),
        supabase.from("tier_limits").select("*"),
        supabase.from("risk_scores").select("fraud_score, trust_score, dispute_rate, completion_rate").eq("user_id", user.id).maybeSingle(),
        supabase.from("gigs").select("id", { count: "exact", head: true }).eq("worker_id", user.id).eq("status", "completed"),
      ]);
      if (p) {
        setTier((p as any).tier ?? "basic");
        setKyc((p as any).kyc_status ?? "unverified");
        const days = Math.floor((Date.now() - new Date((p as any).created_at).getTime()) / 86400000);
        setStats({ completed: c ?? 0, account_age_days: days });
      }
      setLimits(((l as Limits[]) ?? []).sort((a, b) => TIER_ORDER.indexOf(a.tier) - TIER_ORDER.indexOf(b.tier)));
      setRisk(r as Risk | null);
      setLoading(false);
    })();
  }, [user]);

  const myLimits = limits.find((l) => l.tier === tier);
  const nextTier = TIER_ORDER[TIER_ORDER.indexOf(tier) + 1];

  return (
    <MobileShell>
      <BrandHeader onBack={() => navigate(-1)} />
      <div className="px-4 pt-2 pb-6">
        <h1 className="font-display font-extrabold text-2xl">Verification</h1>
        <p className="text-xs text-muted-foreground">Earn trust. Unlock bigger jobs and faster payouts.</p>
      </div>

      {loading ? (
        <div className="grid place-items-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="px-4 pb-24 space-y-4">
          {/* Current tier card */}
          <div className="rounded-2xl bg-card-gradient text-primary-foreground p-5">
            <p className="text-xs uppercase opacity-70">Your tier</p>
            <div className="mt-1 flex items-center gap-2">
              <TierBadge tier={tier} className="bg-white/15 text-white border-white/20" />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
              <div>
                <p className="opacity-70">Trust score</p>
                <p className="font-display font-extrabold text-2xl">{risk?.trust_score ?? 50}</p>
              </div>
              <div>
                <p className="opacity-70">Completed gigs</p>
                <p className="font-display font-extrabold text-2xl">{stats.completed}</p>
              </div>
            </div>
          </div>

          {/* My limits */}
          {myLimits && (
            <div className="rounded-2xl bg-card border border-border p-4 space-y-2">
              <p className="text-sm font-semibold">Your limits</p>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><p className="text-muted-foreground">Daily payout</p><p className="font-display font-bold text-lg">{formatMoney(myLimits.daily_payout_cents)}</p></div>
                <div><p className="text-muted-foreground">Max gig budget</p><p className="font-display font-bold text-lg">{formatMoney(myLimits.max_gig_budget_cents)}</p></div>
              </div>
              {myLimits.instant_release && <p className="text-xs text-success">⚡ Instant escrow release enabled</p>}
            </div>
          )}

          {/* Next tier */}
          {nextTier && (
            <div className="rounded-2xl bg-card border border-border p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold">Next: <TierBadge tier={nextTier} compact /></p>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </div>
              <ul className="space-y-1 text-xs">
                {TIER_REQ[nextTier].map((req) => (
                  <li key={req} className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" /> {req}
                  </li>
                ))}
              </ul>
              {nextTier === "verified" && kyc !== "approved" && (
                <Button asChild className="w-full rounded-full">
                  <Link to="/kyc"><IdCard className="h-4 w-4 mr-2" /> {kyc === "pending" ? "KYC pending review" : "Start ID verification"}</Link>
                </Button>
              )}
            </div>
          )}

          {/* All tiers */}
          <div className="space-y-2">
            <p className="text-sm font-semibold">All tiers</p>
            {limits.map((l) => {
              const Icon = l.tier === "elite" ? Crown : l.tier === "pro" ? Star : l.tier === "verified" ? ShieldCheck : Shield;
              return (
                <div key={l.tier} className="rounded-2xl bg-card border border-border p-3 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-muted grid place-items-center"><Icon className="h-5 w-5" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold capitalize">{l.tier}</p>
                    <p className="text-[11px] text-muted-foreground">{l.description}</p>
                  </div>
                  <div className="text-right text-[10px] text-muted-foreground">
                    <p>up to {formatMoney(l.max_gig_budget_cents)}</p>
                    <p>{formatMoney(l.daily_payout_cents)}/day</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </MobileShell>
  );
};

export default Verification;
